import React, { createContext, ReactElement, ReactNode, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
export interface WarningMessage {
  readonly id: string;
  readonly title: string;
  readonly message: string;
}

interface WarningMessageContextType {
  readonly warnings: readonly WarningMessage[];
  readonly addWarning: (title: string, message: string) => void;
  readonly removeWarning: (id: string) => void;
  readonly clearWarnings: () => void;
}

const WarningMessageContext = createContext<WarningMessageContextType | null>(null);

export interface WarningMessageProviderProps {
  readonly children: ReactNode;
}

export function WarningMessageProvider({ children }: WarningMessageProviderProps): ReactElement {
  const [warnings, setWarnings] = useState<readonly WarningMessage[]>([]);

  const addWarning = useCallback((title: string, message: string) => {
    const id = uuidv4();
    setWarnings(prev => [...prev, { id, title, message }]);
  }, []);

  const removeWarning = useCallback((id: string) => {
    setWarnings(prev => prev.filter(warning => warning.id !== id));
  }, []);

  const clearWarnings = useCallback(() => {
    setWarnings([]);
  }, []);

  return (
    <WarningMessageContext.Provider value={{ warnings, addWarning, removeWarning, clearWarnings }}>
      {children}
    </WarningMessageContext.Provider>
  );
}

export function useWarningMessage(): WarningMessageContextType {
  const context = useContext(WarningMessageContext);
  if (!context) {
    throw new Error('useWarningMessage must be called within a WarningMessageProvider');
  }
  return context;
}
